import { Progress, Select, Tooltip } from 'antd';
import { observer } from 'mobx-react';
import moment from 'moment-timezone';
import React from 'react';
import { Button } from '../../components/button';
import { NotificationManager } from '../../components/notification-manager';
import { PageTopBar } from '../../components/page-top-bar';
import { Tag } from '../../components/tag';
import { useDoc31Store } from '../../contexts/mobx';
import { Doc31EditionStatus } from '../../types/doc31';
import {
    DOC31_DEFAULT_PLAY_DAYS,
    DOC31_DEFAULT_TIMEZONE,
    DOC31_PAGE_TITLE,
    DOC31_STATUS_LABELS,
    DOC31_STATUS_TAGS,
    DOC31_TRANSITIONS,
    Doc31PlayDays,
    Doc31Transition,
    doc31PlayDays,
} from '../../utils/doc31';
import style from './edition-header.module.scss';
import { Doc31ReasonPrompt } from './reason-prompt';

/** What each move does, in the reason prompt. */
const TRANSITION_DESCRIPTIONS: Record<Doc31EditionStatus, string> = {
    draft: 'Players no longer see the game until it is scheduled again.',
    scheduled: 'Players can see the game. Doors open when play starts.',
    active: 'Doors open during the play window.',
    paused: 'Doors stop opening. Coupons already won still work.',
    earning_closed: 'Doors and bonuses stop for good. Redemption stays open.',
    closed: 'The edition becomes read-only history.',
    archived: 'The edition leaves daily use.',
};

const DANGER_TARGETS: Partial<Record<Doc31EditionStatus, true>> = {
    earning_closed: true,
    closed: true,
    archived: true,
};

const transitionVariant = (transition: Doc31Transition) => {
    if (DANGER_TARGETS[transition.target]) {
        return 'secondary-destructive';
    }
    return transition.primary ? 'primary' : 'secondary';
};

const day = (date: string, timezone: string) =>
    moment.tz(date, timezone).format('MMM D');

function headline(days: Doc31PlayDays, timezone: string): string {
    switch (days.phase) {
        case 'upcoming':
            return `Starts ${day(days.first, timezone)}`;
        case 'running':
            return `Door ${days.door} of ${days.count}`;
        case 'ended':
            return `Play ended ${day(days.last, timezone)}`;
    }
}

function openedShare(days: Doc31PlayDays): number {
    switch (days.phase) {
        case 'upcoming':
            return 0;
        case 'running':
            return ((days.door ?? 0) / days.count) * 100;
        case 'ended':
            return 100;
    }
}

interface EditionHeaderProps {
    reasonPrompt: Doc31ReasonPrompt;
    onSwitchEdition: (key: string) => void;
    /** A move was refused for open setup items: show them. */
    onShowReadiness: () => void;
}

/**
 * The CMS page bar (edition picker, state) and, under it, where play stands:
 * today's door, the window, the last save, and the lifecycle moves. The moves
 * stay off the bar, where other pages put Save. Dates and Play days being
 * edited in Setup show here before they are saved.
 */
export const EditionHeader = observer(
    ({
        reasonPrompt,
        onSwitchEdition,
        onShowReadiness,
    }: EditionHeaderProps) => {
        const store = useDoc31Store();
        const { view, status } = store;
        const timezone = view?.timezone ?? DOC31_DEFAULT_TIMEZONE;
        const playWindow =
            store.draftPlayWindow ?? view?.configuration.edition ?? null;
        const days = playWindow
            ? doc31PlayDays({ ...playWindow, timezone }, new Date())
            : null;
        // The Play days being edited in Game rules count before they save.
        const maxDays =
            store.draftMaxPlayDays !== undefined
                ? store.draftMaxPlayDays ?? DOC31_DEFAULT_PLAY_DAYS
                : view?.configuration.rules.maxPlayDays ??
                  DOC31_DEFAULT_PLAY_DAYS;
        // Primary first: the moves read left to right, under the summary.
        const transitions = status
            ? [...DOC31_TRANSITIONS[status]].sort(
                  (a, b) => Number(b.primary) - Number(a.primary)
              )
            : [];
        const isSetupValid = view?.validation.valid ?? false;

        const run = async (transition: Doc31Transition) => {
            const reason = await reasonPrompt.prompt({
                actionLabel: transition.label,
                description: TRANSITION_DESCRIPTIONS[transition.target],
                danger: DANGER_TARGETS[transition.target],
            });
            if (!reason) {
                return;
            }
            const result = await store.transition(transition.target, reason);
            if (result.ok) {
                NotificationManager.showSuccess(`${transition.done}.`);
            } else if (result.validation) {
                onShowReadiness();
            }
        };

        const actions = transitions.length > 0 && (
            <div
                className={style.actions}
                role="group"
                aria-label="Edition state"
            >
                {transitions.map((transition) => {
                    const blocked = transition.needsValidSetup && !isSetupValid;
                    const button = (
                        <Button
                            key={transition.target}
                            variant={transitionVariant(transition)}
                            disabled={blocked || store.isSaving}
                            onClick={() => run(transition)}
                        >
                            {transition.label}
                        </Button>
                    );
                    return blocked ? (
                        <Tooltip
                            key={transition.target}
                            title="Fix the setup checks first."
                        >
                            <span>{button}</span>
                        </Tooltip>
                    ) : (
                        button
                    );
                })}
            </div>
        );

        return (
            <>
                <PageTopBar
                    title={DOC31_PAGE_TITLE}
                    titleExtra={
                        <>
                            <Select
                                className={style.edition_select}
                                value={store.editionKey ?? undefined}
                                onChange={onSwitchEdition}
                                options={store.editions.map((edition) => ({
                                    value: edition.key,
                                    label: edition.key,
                                }))}
                                aria-label="Edition"
                            />
                            {status && (
                                <Tag variant={DOC31_STATUS_TAGS[status]}>
                                    {DOC31_STATUS_LABELS[status]}
                                </Tag>
                            )}
                        </>
                    }
                />
                {(view || actions) && (
                    <section className={style.summary} aria-label="Play">
                        {view && (
                            <>
                                <div className={style.summary_line}>
                                    <strong className={style.headline}>
                                        {days
                                            ? headline(days, timezone)
                                            : playWindow
                                            ? 'Play ends before it starts.'
                                            : 'Set the play window'}
                                    </strong>
                                    {days && (
                                        <span className={style.muted}>
                                            {day(days.first, timezone)} →{' '}
                                            {day(days.last, timezone)} ·{' '}
                                            {timezone}
                                            {store.draftPlayWindow &&
                                                ' · unsaved dates'}
                                        </span>
                                    )}
                                    <span
                                        className={`${style.muted} ${style.saved}`}
                                    >
                                        Version {view.configurationVersion} ·
                                        saved{' '}
                                        {moment
                                            .tz(view.savedAt, timezone)
                                            .format('MMM D, HH:mm')}
                                    </span>
                                </div>
                                {days && (
                                    <Progress
                                        className={style.progress}
                                        percent={openedShare(days)}
                                        showInfo={false}
                                        size="small"
                                        strokeColor="var(--Primary-6)"
                                        trailColor="var(--Neutral-4)"
                                        aria-label={headline(days, timezone)}
                                    />
                                )}
                                {days && days.count > maxDays && (
                                    <p className={style.problem}>
                                        {days.count} days: this edition allows{' '}
                                        {maxDays}. Shorten the play window or
                                        raise Play days in Game rules.
                                    </p>
                                )}
                                {status === 'paused' &&
                                    store.state?.pauseReason && (
                                        <p className={style.muted}>
                                            Paused: “{store.state.pauseReason}”
                                        </p>
                                    )}
                            </>
                        )}
                        {actions}
                    </section>
                )}
            </>
        );
    }
);
